import { FunctionComponent, ReactNode, useState } from 'react'
import styled from '@emotion/styled'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import EmptySearchResult, { EmptySearchResultVariant } from '@/components/EmptySearchResult'
import Pagination from '@/components/Pagination'
import { OrderMethod, parseHTMLEntities } from '@/libs/helpers/utils'
import type { SearchVenue } from '@/providers/state/search'
import { Button, Grid, Text } from '@/tbui'

export { EmptySearchResultVariant }

const StyledTitle = styled.h2`
  font-family: ${(props) => props.theme.font.FAMILY_BOLD};
  letter-spacing: 0.6px;
  ${(props) =>
    props.theme.mixin.fluidRange({
      prop: 'fontSize',
      fromSize: '18px',
      toSize: '30px',
    })}
`

const StyledResultName = styled(Text)`
  font-family: ${(props) => props.theme.font.FAMILY_BOLD};
  color: ${(props) => props.theme.palette.PRIMARY};
`

const StyledAnchor = styled.a`
  border-radius: ${(props) => props.theme.shape.RADIUS};
  transition: 0.25s ease;
  text-decoration: none;
  padding: 16px;
  &:hover {
    background: ${(props) => props.theme.palette.SECONDARY};
  }
`

const SearchResultsComponent: FunctionComponent<{
  results: SearchVenue[]
  itemsPerPage?: number
  title: string
  orderMethod: OrderMethod
}> = ({ itemsPerPage, results, title, orderMethod }) => {
  const [currentPage, setCurrentPage] = useState<number>(0)
  const { t } = useTranslation()
  const router = useRouter()

  const totalItems = results.length
  const totalPages = Math.ceil(totalItems / Number(itemsPerPage) - 1)
  const isLastPage = currentPage === totalPages

  const renderDistanceAway = (distance: number, country: string): ReactNode => {
    let formattedDistance

    if (country === 'US') {
      const roundedDistance = Math.round((distance * 10) / 1.609) / 10
      formattedDistance = t('tbdine.search_results.distance', {
        distance: roundedDistance,
        context: 'miles',
      })
    } else {
      const roundedDistance = Math.round(distance * 10) / 10
      formattedDistance = t('tbdine.search_results.distance', {
        distance: roundedDistance,
        context: 'km',
      })
    }

    if (distance < 1) {
      if (country === 'US') {
        formattedDistance = t('tbdine.search_results.distance', {
          distance: Math.round((distance * 100) / 1.609) / 100,
          context: 'miles',
        })
      } else {
        // Convert distance to meters if less than 1KM away
        const meters = distance * 1000
        formattedDistance = t('tbdine.search_results.distance', {
          distance: Math.round(meters),
          context: 'm',
        })
      }
    }

    return <Text>{formattedDistance}</Text>
  }

  const renderResult = (venue: SearchVenue): ReactNode => {
    return (
      <StyledAnchor href={venue.orderingURL} key={venue.orderingURL} target="_blank">
        <Grid align="stretch" gap="4px">
          <StyledResultName>{parseHTMLEntities(venue.venueName)}</StyledResultName>
          <Text color="GRAY_3">
            {parseHTMLEntities(`${venue.venueAddress1}, ${venue.venueCity} ${venue.venueState}`)}
          </Text>
          {renderDistanceAway(venue.distanceKM, venue.venueCountry)}
        </Grid>
      </StyledAnchor>
    )
  }

  const renderSeeAllButton = (): ReactNode => {
    if (!isLastPage) {
      return null
    }
    const handleClickSeeAll = async (): Promise<void> => {
      await router.push('/[orderMethod]/all', `/${orderMethod.toLowerCase()}/all`)
    }

    return (
      <Grid align="center" p="16px">
        <Button fullWidth variant="OUTLINE" onClick={handleClickSeeAll}>
          {t('tbdine.search_results.see_all')}
        </Button>
      </Grid>
    )
  }

  const getItemsPerPage = (): SearchVenue[] => {
    if (!itemsPerPage) {
      return results
    }
    const start = currentPage * itemsPerPage
    const end = start + itemsPerPage
    return results.slice(start, end)
  }

  const renderResultPage = (): ReactNode => {
    if (!totalItems) {
      return <EmptySearchResult variant={EmptySearchResultVariant.RESTAURANT_AROUND} />
    }

    const itemsToShow = getItemsPerPage()
    if (itemsPerPage && isLastPage && !(totalItems % itemsPerPage)) {
      itemsToShow.pop()
    }

    return (
      <Grid xs={1} sm={2} md={3} lg={4} gap="20px">
        {itemsToShow.map(renderResult)}
        {renderSeeAllButton()}
      </Grid>
    )
  }

  const renderPagination = (): ReactNode => {
    if (!itemsPerPage || !totalItems || !totalPages) {
      return null
    }
    return (
      <Grid justify="end">
        <Pagination
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onChange={setCurrentPage}
          total={totalItems}
        />
      </Grid>
    )
  }

  return (
    <Grid container p="40px 24px">
      <Grid justify="space-between" align="center">
        <StyledTitle>{title}</StyledTitle>
      </Grid>
      {renderResultPage()}
      {renderPagination()}
    </Grid>
  )
}

export default SearchResultsComponent
